/**
 * @fileoverview Generic clickable "view" card for a hub/landing page.
 * @description Title + description at the top, a headline metric + trend pill + directional icon
 * circle at the bottom, with a faint directional color wash across the whole card so its overall
 * sentiment reads at a glance across a grid of them. Ported from the InsightBox Banking design
 * system's own `ViewGridCard`, generalized (icons inlined to match this app's own icon convention
 * instead of `lucide-react`). Distinct from the existing `SummaryCardShell` (icon-badge + title +
 * value/caption + a middle "visual" slot) — this card's trend-pill-and-wash treatment is a
 * different pattern, used for a hub page's small set of "go to this sub-view" cards rather than a
 * dashboard's page-preview cards.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { Link } from 'react-router'

import Skeleton from '@/shared/components/atoms/Skeleton'

interface ViewGridCardTrend {
  direction: 'up' | 'down' | 'flat'
  text: string
  positiveIsGood?: boolean
}

interface ViewGridCardProps {
  to: string
  title: string
  description: string
  metricLabel: string
  metricValue: string
  trend: ViewGridCardTrend
  loading?: boolean
}

const TREND_ICON_PATH: Record<'up' | 'down' | 'flat', string> = {
  up: 'M17 7l-8.5 8.5L4 11M17 7h-5M17 7v5',
  down: 'M17 17l-8.5-8.5L4 13M17 17h-5M17 17v-5',
  flat: 'M5 12h14',
}

/**
 * @component
 * @param {ViewGridCardProps} props - The card's copy, destination, and headline metric/trend.
 * @param {string} props.to - The route this card navigates to when clicked.
 * @param {string} props.title - The card's title.
 * @param {string} props.description - The one-line description under the title.
 * @param {string} props.metricLabel - The small label above the headline value.
 * @param {string} props.metricValue - The headline value.
 * @param {ViewGridCardTrend} props.trend - The trend direction/text shown as a pill next to the
 * value, and as an icon-circle at the card's bottom right.
 * @param {boolean} [props.loading] - Shows a shimmer in place of the metric value/trend pill. Off
 * by default.
 */
function ViewGridCard({
  to,
  title,
  description,
  metricLabel,
  metricValue,
  trend,
  loading = false,
}: ViewGridCardProps) {
  const trendGood =
    trend.direction === 'flat'
      ? null
      : (trend.direction === 'up') === (trend.positiveIsGood ?? true)
  const washClassName =
    trend.direction !== 'flat'
      ? trendGood
        ? 'bg-gradient-to-l from-success/[0.06] to-transparent'
        : 'bg-gradient-to-l from-error/[0.06] to-transparent'
      : ''
  const iconDirection: 'up' | 'down' | 'flat' =
    trend.direction === 'flat' ? 'flat' : trendGood ? 'up' : 'down'
  const tintClassName =
    trend.direction === 'flat'
      ? 'bg-input-bg text-label'
      : trendGood
        ? 'bg-green-50 text-success'
        : 'bg-red-50 text-error'
  // Deeper, solid-color pill (white text) so the trend number carries more visual weight than the
  // icon circle's lighter tint.
  const pillClassName =
    trend.direction === 'flat'
      ? 'bg-input-bg text-label'
      : trendGood
        ? 'bg-success text-white'
        : 'bg-error text-white'

  return (
    <Link
      to={to}
      className={`group flex flex-col justify-between rounded-2xl border border-border bg-white p-6 shadow-card transition hover:-translate-y-0.5 hover:border-primary hover:shadow-lg ${washClassName}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-text md:text-lg">{title}</h3>
          <p className="mt-1 text-xs text-label md:text-sm">{description}</p>
        </div>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="mt-1 shrink-0 text-text-disabled transition group-hover:translate-x-0.5 group-hover:text-primary"
          aria-hidden="true"
        >
          <path d="M4.5 12h15M13 5.5L19.5 12 13 18.5" />
        </svg>
      </div>

      <div className="mt-5 flex items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold tracking-wide text-label uppercase">
            {metricLabel}
          </p>
          <div className="mt-1 flex items-center gap-2">
            {loading ? (
              <Skeleton width={80} height={28} />
            ) : (
              <p className="text-2xl font-extrabold text-text md:text-[28px]">{metricValue}</p>
            )}
            {!loading && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${pillClassName}`}
              >
                {iconDirection !== 'flat' && (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path
                      d={iconDirection === 'up' ? 'M12 19V5M5 12l7-7 7 7' : 'M12 5v14M5 12l7 7 7-7'}
                    />
                  </svg>
                )}
                {trend.text}
              </span>
            )}
          </div>
        </div>

        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tintClassName}`}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d={TREND_ICON_PATH[iconDirection]} />
          </svg>
        </span>
      </div>
    </Link>
  )
}

export default ViewGridCard
